/**
 * Per-site font customization. Mirrors the admin/backend `FontScheme` shape.
 * The uploader uses exactly two fonts — an elegant `heading` font for `h1` and
 * a readable `body` font for everything else — applied via the `--font-heading`
 * and `--font-body` CSS variables defined in src/index.scss.
 */
export interface FontSelection {
  family: string;
  category: string;
}

export interface FontScheme {
  heading: FontSelection;
  body: FontSelection;
  /** Prebuilt Google Fonts CSS2 link covering both families. */
  cssUrl: string;
}

const GOOGLE_FONTS_CSS_PREFIX = "https://fonts.googleapis.com/css2?";
const FONT_FAMILY_RE = /^[A-Za-z0-9]+(?: [A-Za-z0-9]+)*$/;
const FONT_PREVIEW_LINK_ID = "site-fonts";

/** Maps a Google Fonts category to a CSS generic fallback family. */
function genericFallback(category: string): string {
  switch (category) {
    case "serif":
      return "serif";
    case "monospace":
      return "monospace";
    case "handwriting":
      return "cursive";
    case "display":
      return "system-ui";
    default:
      return "sans-serif";
  }
}

/** Builds a safe CSS `font-family` stack for a single selection. */
function fontStack(selection: FontSelection): string | null {
  const family = selection.family.trim();
  if (!FONT_FAMILY_RE.test(family)) return null;
  return `"${family}", ${genericFallback(selection.category)}`;
}

/**
 * Applies a site's font scheme by injecting the Google Fonts stylesheet and
 * overriding the `--font-heading` / `--font-body` CSS variables. Invalid input
 * is ignored so a bad config can never break rendering — the static defaults in
 * index.scss remain the fallback.
 */
export function applyFontScheme(fontScheme: FontScheme | undefined | null): void {
  if (!fontScheme || typeof fontScheme !== "object") return;
  const { heading, body, cssUrl } = fontScheme;
  if (!heading || !body) return;

  if (typeof cssUrl === "string" && cssUrl.startsWith(GOOGLE_FONTS_CSS_PREFIX)) {
    let link = document.getElementById(
      FONT_PREVIEW_LINK_ID,
    ) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = FONT_PREVIEW_LINK_ID;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== cssUrl) link.href = cssUrl;
  }

  const root = document.documentElement;
  const headingStack = fontStack(heading);
  const bodyStack = fontStack(body);
  if (headingStack) root.style.setProperty("--font-heading", headingStack);
  if (bodyStack) root.style.setProperty("--font-body", bodyStack);
}
